import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { loadStripe } from '@stripe/stripe-js/pure';
import { ActionStatus } from 'thunkless';

import {
  orgLoadingStatusSelector,
  orgStripeCustomerPortalUrlSelector,
  orgStripeSessionSelector,
  orgPlanSelector,
  orgSubscriptionEndSelector,
} from '../../../redux/app/selectors/organization';
import {
  stripeCustomerPortal,
  getOrgProps,
  getSeatSummary,
  getUpgradeSummary,
  setCheckoutSummary,
  clearOrgStatuses,
} from '../../../redux/app/actions/organization';
import { NavigationPaneRouteParamsContext } from '../../../lib/navigation';
import { InvoiceSection } from './InvoiceSection';
import { ManagementSection } from './ManagementSection';
import { AppSecretsContext } from '../secrets/app-secrets';
import { UILoader } from '../ui-elements/UILoader';
import { CheckoutFlow } from '../checkout/CheckoutFlow';
import { UpgradeFlow } from '../checkout/UpgradeFlow';
import { ConfirmationModal } from '../shared/ConfirmationModal';
import { BalanceUsagePane } from './BalanceUsagePane';
import { BillingPaneHeaders } from './BillingPaneHeaders';
import { CancellationModal } from './CancellationModal';
import { SubscriptionDetailsPlanSection } from './SubscriptionDetailsPlanSection';
import { SubscriptionDetailsBalanceSection } from './SubscriptionDetailsBalanceSection';
import { SubscriptionDetailsSeatSection } from './SubscriptionDetailsSeatSection';
import { SubscriptionDetailsQuickAddOnSection } from './SubscriptionDetailsQuickAddOnSection';

export const Billing = () => {
  const dispatch = useDispatch();
  const [pane, setPane] = React.useState('subscription');
  const [checkoutType, setCheckoutType] = React.useState('');
  const { subdomain } = React.useContext(NavigationPaneRouteParamsContext);
  const { stripePublishableKey } = React.useContext(AppSecretsContext);

  const loadingStatus = useSelector(orgLoadingStatusSelector);
  const plan = useSelector(orgPlanSelector);
  const stripeSessionId = useSelector(orgStripeSessionSelector);
  const stripeCustomerPortalSessionUrl = useSelector(orgStripeCustomerPortalUrlSelector);
  const subscriptionEnd = useSelector(orgSubscriptionEndSelector);

  const [isShowUpgradeModal, setIsShowUpgradeModal] = React.useState(false);
  const [isShowCancelModal, setIsShowCancelModal] = React.useState(false);
  const [isShowContactSupport, setIsShowContactSupport] = React.useState(false);

  const isTrialPlan = plan === 'trial-plan-4';

  React.useEffect(() => {
    dispatch(getOrgProps({ subdomain }));
    document.title = 'Billing | Avochato';

    Object.keys(window.Avochato.widgets).forEach((key) => {
      window.Avochato.widgets[key].hide();
    });

    return () => { dispatch(clearOrgStatuses()); };
  }, []);

  React.useEffect(() => {
    if (!stripeSessionId) return;
    if (!window.navigator.userAgent) return;

    // skip loading stripe on iphone/ipad
    if (window.navigator.userAgent.match(/Avochato for i(Pad|Phone)/)) return;

    const redirectToStripe = async () => {
      try {
        const response = await loadStripe(stripePublishableKey);
        response.redirectToCheckout({
          sessionId: stripeSessionId,
        });
      } catch (err) {
        console.log(err);
      }
    };

    redirectToStripe();
  }, [stripeSessionId, stripePublishableKey]);

  React.useEffect(() => {
    if (!stripeCustomerPortalSessionUrl) return;
    window.location.href = stripeCustomerPortalSessionUrl;
  }, [stripeCustomerPortalSessionUrl]);

  const isUpgradeAvailable = plan === 'lite-plan-4';
  const handleUpdateCreditCardClick = () => (
    dispatch(stripeCustomerPortal({ subdomain, updateCreditCard: true }))
  );
  const handleInvoicesClick = () => dispatch(stripeCustomerPortal({ subdomain }));
  const handleCheckout = (e) => {
    const selectedCheckoutType = e.currentTarget.dataset.checkouttype;
    switch (selectedCheckoutType) {
      case 'addSeats':
        dispatch(getSeatSummary({ subdomain }));
        break;
      case 'addPlanUpgrade':
        dispatch(getUpgradeSummary({ subdomain }));
        break;
      case 'addBalance':
        /* eslint-disable no-case-declarations */
        const defaultBalance = 25;
        dispatch(setCheckoutSummary({
          checkout: {
            lineItems: {
              balance: {
                id: 'balance',
                name: 'Balance',
                unitCost: `$${defaultBalance}`,
                quantity: 1,
                total: `$${defaultBalance}`,
              },
            },
            totalToday: `$${defaultBalance}`,
            balance: defaultBalance,
          },
        }));
        break;
      default:
        break;
    }

    setCheckoutType(selectedCheckoutType);
  };

  const handleUpgradeClick = (e) => {
    if (isTrialPlan) {
      setIsShowUpgradeModal(true);
      return;
    }
    if (!isUpgradeAvailable) {
      setIsShowContactSupport(true);
      return;
    }
    handleCheckout(e);
  };
  const handleConfirmContactSupport = () => {
    setIsShowContactSupport(false);
    Object.keys(window.Avochato.widgets).forEach((key) => {
      window.Avochato.widgets[key].openWithOption('current customer');
    });
  };
  const handleClearCheckoutType = () => setCheckoutType('');

  if (!loadingStatus || loadingStatus === ActionStatus.BUSY) {
    return <div className="v4-billing avo-ui-3" style={{ height: '100%' }}><UILoader /></div>;
  }

  if (checkoutType) {
    return (
      <UpgradeFlow
        checkoutType={checkoutType}
        handleClearCheckoutType={handleClearCheckoutType}
      />
    );
  }

  return (
    <div className="v4-billing avo-ui-3">
      { isShowUpgradeModal && (
        <CheckoutFlow handleCloseModal={() => setIsShowUpgradeModal(false)} />
      )}
      { isShowCancelModal && <CancellationModal hideModal={() => setIsShowCancelModal(false)} /> }

      <ConfirmationModal
        showModal={isShowContactSupport}
        handleHideModal={() => setIsShowContactSupport(false)}
        handleEndSetup={handleConfirmContactSupport}
        header="Upgrade to a Custom Plan"
        question="You’re currently on the Standard Plan. Please contact us to expand your usage or get custom pricing."
        content=""
        cancelText="Nevermind"
        confirmText="Let's Talk"
        buttonColor="bright-green"
      />
      <div id="billing-portal">
        <h2>Billing</h2>
        <BillingPaneHeaders setPane={setPane} pane={pane} />
        <hr />
        {pane === 'usage' && <BalanceUsagePane />}
        {pane === 'subscription' && (
          <>
            <div className="three-col-section">
              <p className="orange bold-900 mt-25 mb-15">Subscription Details</p>
              <SubscriptionDetailsPlanSection handleClick={handleUpgradeClick} />
              <SubscriptionDetailsBalanceSection />
              <SubscriptionDetailsSeatSection handleClick={handleCheckout} />
            </div>
            <div className="two-row-section">
              <p className="orange bold-900 my-15">Quick Add-On</p>
              <SubscriptionDetailsQuickAddOnSection handleClick={handleCheckout} />
            </div>
            <div className="management">
              <p className="orange bold-900 my-15">Management</p>
              <InvoiceSection
                handleUpdateCreditCardClick={handleUpdateCreditCardClick}
                handleInvoicesClick={handleInvoicesClick}
              />
              <ManagementSection
                title="Cancel Subscription"
                description={`Continue to use Avochato until ${subscriptionEnd}.  Important: You will not be able access your balance without a subscription to at least 1 Avochato Seat.`}
                buttonName="Cancel Subscription"
                buttonColor="red"
                clickHandler={() => setIsShowCancelModal(true)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
